import { useEffect, type JSX } from 'react';

import { ModeChrome } from './ModeChrome.js';
import { useContextPanelStatus } from './useContextPanelStatus.js';
import { useRootStore } from '../../state/store.js';
import { navigateTo } from '../../router/hash-router.js';

export function GitMode(): JSX.Element {
  const status = useContextPanelStatus('git');
  const overview = useRootStore((s) => s.gitOverview);
  const loadOverview = useRootStore((s) => s.loadGitOverview);
  const loadDiff = useRootStore((s) => s.loadGitDiff);

  useEffect(() => {
    void loadOverview();
  }, [loadOverview]);

  return (
    <ModeChrome
      modeKey="git"
      testId="git-mode-root"
      title="Git"
      emptyStateText="No git repository detected for this workspace."
      errorText="Could not load git workspace details."
      status={status}
    >
      {overview !== null ? (
        <div className="flex h-full flex-col">
          <div className="border-b border-taupe-300 px-3 py-2 text-xs text-taupe-800">
            <div className="font-semibold text-stone-900">
              {overview.currentBranch ?? 'No active branch'}
            </div>
            <div className="mt-1 text-[11px] text-taupe-700">
              {overview.workingTree.dirty
                ? `${overview.workingTree.modified.length + overview.workingTree.staged.length} local change(s)`
                : 'Working tree clean'}
            </div>
          </div>

          <section className="border-b border-taupe-300 px-2 py-2">
            <h3 className="px-1 text-[11px] font-semibold uppercase tracking-wide text-taupe-700">
              Branches
            </h3>
            <ul className="mt-1 space-y-1">
              {overview.branches.slice(0, 8).map((branch) => (
                <li key={branch.name}>
                  <button
                    type="button"
                    className="flex w-full items-center justify-between rounded px-2 py-1 text-left text-[12px] hover:bg-cream-200"
                    onClick={() => {
                      void loadDiff({
                        base: overview.currentBranch ?? 'HEAD',
                        target: branch.name,
                      });
                      navigateTo({ kind: 'git' });
                    }}
                  >
                    <span className="truncate text-stone-900">{branch.name}</span>
                    <span className="ml-2 text-[10px] text-taupe-700">
                      {branch.isMerged ? 'merged' : branch.isCurrent ? 'current' : 'compare'}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </section>

          <section className="min-h-0 flex-1 overflow-auto px-2 py-2">
            <h3 className="px-1 text-[11px] font-semibold uppercase tracking-wide text-taupe-700">
              Recent commits
            </h3>
            <ul className="mt-1 space-y-1">
              {overview.graph.nodes.slice(0, 10).map((node) => (
                <li key={node.sha}>
                  <button
                    type="button"
                    className="w-full rounded px-2 py-1 text-left hover:bg-cream-200"
                    onClick={() => {
                      const parent = node.parentShas[0];
                      if (parent !== undefined) {
                        void loadDiff({ base: parent, target: node.sha });
                      }
                      navigateTo({ kind: 'git' });
                    }}
                  >
                    <div className="truncate text-[12px] text-stone-900">{node.message}</div>
                    <div className="text-[10px] text-taupe-700">
                      {node.shortSha} · {formatRelative(node.date)}
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          </section>
        </div>
      ) : undefined}
    </ModeChrome>
  );
}

function formatRelative(iso: string): string {
  const timestamp = Date.parse(iso);
  if (Number.isNaN(timestamp)) return iso;
  const deltaMs = Date.now() - timestamp;
  const deltaHours = Math.round(deltaMs / (1000 * 60 * 60));
  if (deltaHours < 24) return `${String(Math.max(1, deltaHours))}h ago`;
  const deltaDays = Math.round(deltaHours / 24);
  if (deltaDays < 30) return `${String(deltaDays)}d ago`;
  return new Date(iso).toLocaleDateString();
}
